import React, { useState } from 'react';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import { get_api_url } from '../../../../utils/urls';
import { post_api } from '../../../../redux/api_funcs';
import PayTimeWarningStep from '../../../util_components/pay_time_warning';
import usePayTimeWarning from '../../../util_components/pay_time_warning/use_pay_time_warning';
import { with_pay_time_ack } from '../../../util_components/pay_time_warning/pay_time_warning_rules';

// PROD-8055 / PROD-8463: move a member's next billing date. Care picks a calendar day only; the API
// lands it on the member's billing hour, so no time is picked or sent (adjust_membership_date rejects one).
function ChangeNextBillingDate({
  student_uuid,
  current_next_billing_date,
  onClose,
  onSuccess,
  notifySuccess,
  notifyFailure
}) {
  const [next_billing_date, set_next_billing_date] = useState(null);
  const [reason, set_reason] = useState('');
  const [loading, set_loading] = useState(false);
  const pw = usePayTimeWarning();

  const submit_disabled = loading || !next_billing_date || reason.trim().length === 0;

  // Tomorrow in UTC, rendered as a local Date: the API lands the day on the member's billing hour, and
  // today's has usually passed already. The API refuses a passed instant too, and its message is shown.
  const earliest = moment(moment.utc().add(1, 'day').format('YYYY-MM-DD')).toDate();

  // ack: the pay-time acknowledgement (A-52); null on the first send
  const handle_submit = (ack = null) => {
    const url = get_api_url('adjust_membership_date');
    const payload = with_pay_time_ack(
      {
        student_uuid,
        next_billing_date: moment(next_billing_date).format('YYYY-MM-DD'),
        reason: reason.trim()
      },
      ack
    );
    set_loading(true);
    post_api(url, payload, true)
      .then(() => {
        onClose();
        notifySuccess('Next billing date updated successfully.');
        onSuccess();
      })
      .catch((e) => {
        set_loading(false);
        if (pw.catch_warning(e)) return;
        pw.clear();
        notifyFailure(e, 'Failed to update next billing date. Please try again.');
      });
  };

  if (pw.warning) {
    return (
      <PayTimeWarningStep
        warning={pw.warning}
        busy={loading}
        changeLabel="Change date"
        onAcknowledge={(ack) => handle_submit(ack)}
        onChange={pw.clear}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div
        className="flex items-baseline justify-between gap-3 rounded px-3 py-2 text-sm"
        style={{ background: 'var(--surface-sunken)' }}
      >
        <span style={{ color: 'var(--text-muted)' }}>Current next billing date</span>
        <span className="font-semibold">{current_next_billing_date || 'Not set'}</span>
      </div>

      <label className="flex flex-col gap-1">
        <span className="text-sm font-medium">
          New billing date <span style={{ color: 'var(--state-error)' }}>*</span>
        </span>
        <DatePicker
          selected={next_billing_date}
          onChange={(val) => set_next_billing_date(val)}
          dateFormat="yyyy-MM-dd"
          placeholderText="Select date"
          minDate={earliest}
          // CustomModal's inner container is overflow:auto, which clips the popup — portal it out.
          portalId="datepicker-portal"
          wrapperClassName="w-full"
          className="w-full"
        />
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
          The member is billed on this day at their usual billing hour. Today and past days are
          disabled.
        </span>
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-sm font-medium">
          Reason <span style={{ color: 'var(--state-error)' }}>*</span>
        </span>
        <textarea
          name="change_billing_date_reason"
          value={reason}
          onChange={(e) => set_reason(e.target.value)}
          rows={3}
          className="w-full"
          style={{ resize: 'vertical' }}
          placeholder="Why is the billing date being changed?"
        />
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
          Recorded on the student&apos;s timeline against your name.
        </span>
      </label>

      <div
        className="flex justify-end gap-2 pt-3"
        style={{ borderTop: '1px solid var(--border-default)' }}
      >
        <button type="button" className="button-secondary" onClick={onClose}>
          Cancel
        </button>
        {/* Native disabled for keyboard/AT, button-disabled for the greyed-out look. */}
        <button
          type="button"
          className={submit_disabled ? 'button-disabled' : ''}
          onClick={() => handle_submit()}
          disabled={submit_disabled}
        >
          {loading ? 'Submitting...' : 'Submit'}
        </button>
      </div>
    </div>
  );
}

export default ChangeNextBillingDate;
